// Loan dashboard — Overview tab. Answers "is this loan healthy, and what
// needs doing?" in five seconds:
//   • Vitals strip (balance · next payment · DSCR · delinquency · state ·
//     maturity) + role-aware quick actions — above the fold.
//   • Health scorecard (8 discipline lights) + needs-attention feed (the
//     retired Due Dates tab's successor) — same server-computed severities
//     as the tab badges (LoanHealthService).
//   • Twelve-month payment timeline from the v3 ledger.
//   • The original identity / performance cards reflowed below; the right
//     rail keeps the cross-cutting previews (activity / tasks / documents /
//     messages).
//
// MUST NOT contain (Layout Standard §4.1): call log entries, full task
// list, document list, conversation threads, internal note composer, or
// the full Activity timeline. The 5-event preview is the one allowed
// exception, and it links out to the Activity tab.
//
// The platform is scoped to pre-contribution loans — no pool / segment /
// seasoning-gate content belongs on this tab.

import { Link } from "react-router-dom";
import {
  Badge,
  Card,
  RECORD_RAIL_WIDTH,
  ResponsiveSplit,
} from "@/design-system/components/ui";
import { formatCalendarDate } from "@/design-system/lib/format";
import { OverviewActivityPreview } from "@/components/activity/OverviewActivityPreview";
import { useOpenTaskCountByRelated } from "@/pages/tasks/hooks/useWorkTasks";
import {
  useLoanIntakeBundle,
  useLoanParties,
} from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import { useLoanHealth } from "@/hooks/portfolioAdmin/useLoanHealth";
import { useServicingLedger } from "@/hooks/portfolioAdmin/useServicing";
import { VitalsStrip } from "../overview/VitalsStrip";
import { QuickActionsRow } from "../overview/QuickActionsRow";
import { HealthScorecardCard } from "../overview/HealthScorecardCard";
import { NeedsAttentionCard } from "../overview/NeedsAttentionCard";
import { PaymentTimelineCard } from "../overview/PaymentTimelineCard";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { TpsAssignmentCard } from "@/components/portfolioAdmin/loanDetail/overview/TpsAssignmentCard";
import { AchMandateCard } from "@/components/portfolioAdmin/loanDetail/overview/AchMandateCard";
import { useAuth } from "@/hooks/useAuth";

interface OverviewTabProps {
  loan: LoanSummaryDto;
}

export function OverviewTab({ loan }: OverviewTabProps) {
  const health = useLoanHealth(loan.id);
  const ledger = useServicingLedger(loan.id);

  return (
    <div className="flex flex-col gap-space-4" data-testid="loan-overview-grid">
      <VitalsStrip
        loan={loan}
        ledgerSummary={ledger.isError ? null : ledger.data?.summary}
        ledgerLoading={ledger.isLoading}
        health={health.data}
        healthLoading={health.isLoading}
      />
      <QuickActionsRow loanId={loan.id} />

      <ResponsiveSplit
        gap="md"
        mainMinWidth="34rem"
        railWidth={RECORD_RAIL_WIDTH}
        railGrows={false}
        main={
          <div className="flex flex-col gap-space-3">
            <HealthScorecardCard
              loanId={loan.id}
              health={health.data}
              isLoading={health.isLoading}
              isError={health.isError}
            />
            <PaymentTimelineCard
              loanId={loan.id}
              periods={ledger.data?.periods}
              isLoading={ledger.isLoading}
              isError={ledger.isError}
            />
            <IdentityCards loan={loan} />
        </div>
        }
        rail={
          <div className="grid gap-space-4 min-w-0">
            {/* 🔴 `min-w-0` IS LOAD-BEARING, AND ITS ABSENCE OVERFLOWED THE PAGE.
                This is a GRID ITEM, and a grid item defaults to `min-width:
                auto` — which resolves to its min-content and becomes a FLOOR the
                track cannot go below. Measured at a 1900px window: the rail cell
                was correct at 352.61px, this div floored at 568.53px (set by the
                needs-attention row's nowrap title), painted to x=2062 and gave
                the document 162px of horizontal scroll. 184px at 1440 and 1280.
                With `min-w-0` the div follows its cell to 352.61px and document
                overflow is 0 at all three.

                The parent's `min-w-0` does not cover this: it lets the GRID box
                shrink, while this item's own auto minimum is what floors the
                track inside it. `ResponsiveSplit` is not implicated — it carries
                `min-w-0` on its container and both cells and measured correct;
                nor is `ResponsiveGrid`, whose track is capped at `min(100%, …)`
                and which measured zero spill even with this exact row in it.
                Table & Tile Standard §12.3. */}
            <div className="flex flex-col gap-space-3 min-w-0" data-testid="overview-cross-cutting">
            <NeedsAttentionCard
              loanId={loan.id}
              health={health.data}
              isLoading={health.isLoading}
              isError={health.isError}
            />
            <CrossCuttingColumn loan={loan} />
          </div>
      </div>
        }
      />
    </div>
  );
}

const NOT_SET = "Not set";

const REAL_ESTATE_LABEL: Record<string, string> = {
  None: "Not included",
  Owned: "Included (owned)",
  Leased: "Included (leased)",
};

const PAYMENT_FREQUENCY_LABEL: Record<number, string> = {
  1: "Monthly",
};

/** The original identity / performance fact cards, reflowed into a
 *  two-column grid (data unchanged — Layout Standard §4.1 facts). */
function IdentityCards({ loan }: { loan: LoanSummaryDto }) {
  const parties = useLoanParties(loan.id);
  const intake = useLoanIntakeBundle(loan.id);
  const { hasPermission } = useAuth();

  const activeParties = (parties.data ?? []).filter((p) => p.isActive);
  const roleCounts = new Map<string, number>();
  for (const p of activeParties) {
    roleCounts.set(p.roleOnLoan, (roleCounts.get(p.roleOnLoan) ?? 0) + 1);
  }

  const structure = intake.data?.profile?.structure ?? null;

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 gap-space-3"
      data-testid="overview-identity"
    >
      <Card>
        <SectionHeading>Borrower &amp; parties</SectionHeading>
        <div
          className="text-h4 font-display font-bold text-onyx"
          data-testid="overview-borrower-name"
        >
          {loan.borrowerName ?? NOT_SET}
        </div>
        {roleCounts.size > 0 && (
          <div className="text-small text-gray-mid mt-space-1">
            {[...roleCounts.entries()]
              .map(([role, count]) => `${role} · ${count}`)
              .join("  ·  ")}
          </div>
        )}
        <Link
          to={`/portfolio-admin/loans/${loan.id}?tab=parties`}
          className="text-caption text-gold-shadow hover:underline"
        >
          View all parties →
        </Link>
      </Card>
      <Card>
        <SectionHeading>Collateral</SectionHeading>
        <Facts
          testId="overview-collateral"
          rows={[
            ["Collateral type", structure?.collateralType ?? NOT_SET],
            [
              "Real estate",
              structure?.realEstateIncluded
                ? (REAL_ESTATE_LABEL[structure.realEstateIncluded] ??
                  structure.realEstateIncluded)
                : NOT_SET,
            ],
          ]}
        />
      </Card>
      <Card>
        <SectionHeading>Origination</SectionHeading>
        <dl className="grid grid-cols-2 gap-y-space-1 m-0">
          <dt className="text-caption text-gray-mid uppercase tracking-label">
            Loan number
          </dt>
          <dd className="text-small text-onyx m-0">{loan.loanNumber}</dd>
          <dt className="text-caption text-gray-mid uppercase tracking-label">
            Originating deal
          </dt>
          <dd className="text-small m-0">
            {loan.dealNumber ? (
              <Link
                to={`/deal-flow/deals/${loan.dealId}`}
                className="text-gold-shadow hover:underline"
                data-testid="overview-deal-link"
              >
                {loan.dealNumber}
              </Link>
            ) : (
              <span className="text-onyx">{NOT_SET}</span>
            )}
          </dd>
        </dl>
      </Card>
      <Card>
        <SectionHeading>Servicer</SectionHeading>
        <Facts
          testId="overview-servicer"
          rows={[
            [
              "Payment day",
              loan.usesBusinessDayScheduleRule
                ? "First business day of each month"
                : loan.paymentDay > 0
                  ? `Day ${loan.paymentDay} of the month`
                  : NOT_SET,
            ],
            [
              "Frequency",
              PAYMENT_FREQUENCY_LABEL[loan.paymentFrequency] ??
                `Every ${loan.paymentFrequency} month(s)`,
            ],
            // Generic label by design — servicers are being migrated; never
            // hardcode a servicer name here. A loan-level reference field
            // does not exist yet, so this is always "Not set" today.
            ["Servicer reference", NOT_SET],
          ]}
        />
      </Card>
      <TpsAssignmentCard loan={loan} />
      {hasPermission("Loans.Manage") && <AchMandateCard loan={loan} />}
      <LoanAgeCard loan={loan} />
      <CovenantsCard loan={loan} />
    </div>
  );
}

function LoanAgeCard({ loan }: { loan: LoanSummaryDto }) {
  const first = new Date(loan.firstPaymentDate);
  const now = new Date();
  const monthsSinceFirstPayment = Math.max(
    0,
    (now.getFullYear() - first.getFullYear()) * 12 +
      (now.getMonth() - first.getMonth()),
  );
  const notStarted = first.getTime() > now.getTime();

  return (
    <Card>
      <SectionHeading>Loan age</SectionHeading>
      <div
        className="text-h4 font-display font-bold text-onyx"
        data-testid="overview-loan-age"
      >
        {notStarted
          ? `First payment ${formatCalendarDate(loan.firstPaymentDate)}`
          : `${monthsSinceFirstPayment} month${monthsSinceFirstPayment === 1 ? "" : "s"}`}
      </div>
      <div className="text-small text-gray-mid mt-space-1">
        {loan.paymentPeriodsRecorded} payment period
        {loan.paymentPeriodsRecorded === 1 ? "" : "s"} recorded
      </div>
    </Card>
  );
}

const DSCR_BADGE_STATUS: Record<string, "finalized" | "review" | "error"> = {
  Pass: "finalized",
  Watch: "review",
  Fail: "error",
};

/** Defensive parse: the covenant blob is free-form JSON copied from the
 *  Final Financial Addendum Exhibit C — render whatever key/value pairs it
 *  holds, and degrade to "None recorded" rather than throw. */
function parseCovenantThresholds(json: string | null): [string, string][] | null {
  if (!json) return null;
  try {
    const parsed: unknown = JSON.parse(json);
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    const rows = Object.entries(parsed as Record<string, unknown>)
      .filter(([, v]) => v !== null && typeof v !== "object")
      .map(([k, v]): [string, string] => [k, String(v)]);
    return rows.length > 0 ? rows : null;
  } catch {
    return null;
  }
}

function CovenantsCard({ loan }: { loan: LoanSummaryDto }) {
  const thresholds = parseCovenantThresholds(loan.covenantThresholdsJson);
  const dscrBadge = loan.latestDscrStatus
    ? DSCR_BADGE_STATUS[loan.latestDscrStatus]
    : undefined;

  return (
    <Card>
      <SectionHeading>Covenants</SectionHeading>
      {thresholds ? (
        <Facts testId="overview-covenant-thresholds" rows={thresholds} />
      ) : (
        <p className="text-small text-gray-mid m-0">None recorded</p>
      )}
      <div
        className="flex items-center gap-space-2 mt-space-2"
        data-testid="overview-dscr"
      >
        <span className="text-caption text-gray-mid uppercase tracking-label">
          Latest DSCR
        </span>
        {loan.latestDscr != null ? (
          <>
            <span className="text-small font-bold text-onyx">
              {loan.latestDscr.toFixed(2)}
            </span>
            {dscrBadge && <Badge status={dscrBadge}>{loan.latestDscrStatus}</Badge>}
            {loan.latestDscrPeriodEnd && (
              <span className="text-caption text-gray-mid">
                as of {formatCalendarDate(loan.latestDscrPeriodEnd)}
              </span>
            )}
          </>
        ) : (
          <span className="text-small text-gray-mid">None recorded</span>
        )}
      </div>
    </Card>
  );
}

function CrossCuttingColumn({ loan }: { loan: LoanSummaryDto }) {
  const openTaskCountQuery = useOpenTaskCountByRelated("Loan", loan.id);
  const openTaskCount = openTaskCountQuery.data ?? 0;

  return (
    <>
      <OverviewActivityPreview
        ownerType="loan"
        ownerId={loan.id}
        detailPath={`/portfolio-admin/loans/${loan.id}`}
        testId="loan-activity-preview"
      />
      <Card>
        <SectionHeading>Open tasks</SectionHeading>
        <div className="flex items-baseline justify-between">
          <div className="text-h2 font-display font-bold text-onyx">
            {openTaskCount}
          </div>
          <Link
            to={`/portfolio-admin/loans/${loan.id}?tab=tasks`}
            className="text-caption text-gold-shadow hover:underline"
          >
            See all tasks →
          </Link>
        </div>
      </Card>
      <Card>
        <SectionHeading>Documents</SectionHeading>
        <Link
          to={`/portfolio-admin/loans/${loan.id}?tab=documents`}
          className="text-caption text-gold-shadow hover:underline"
        >
          Open the Documents tab →
        </Link>
      </Card>
      <Card>
        <SectionHeading>Messages</SectionHeading>
        <Link
          to={`/portfolio-admin/loans/${loan.id}?tab=messages`}
          className="text-caption text-gold-shadow hover:underline"
        >
          Open the Messages tab →
        </Link>
      </Card>
    </>
  );
}

function Facts({
  rows,
  testId,
}: {
  rows: [string, string][];
  testId?: string;
}) {
  return (
    <dl className="grid grid-cols-2 gap-y-space-1 m-0" data-testid={testId}>
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-caption text-gray-mid uppercase tracking-label">
            {label}
          </dt>
          <dd className="text-small text-onyx m-0">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow m-0 mb-space-1">
      {children}
    </h3>
  );
}
